import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import type { Observation } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";
import type * as fhirTypes from "fhir/r4";

interface CreateGenericObservationParams {
  encounterId?: string;
  patientId: string;
  category: (typeof Observation.Category)[keyof typeof Observation.Category];
  method: (typeof Observation.Method.values)[keyof typeof Observation.Method.values];
  code: fhirTypes.CodeableConcept;
  status: (typeof Observation.Status)[keyof typeof Observation.Status];
  fhirValue?: {
    valueString: string;
  };
  dataAbsentReason?: (typeof Observation.DataAbsentReason.values)[keyof typeof Observation.DataAbsentReason.values];
}

interface CreateGenericObservationResponse {
  observation: fhirTypes.Observation;
}

export const useCreateGenericObservation = ({
  patientId,
  encounterId,
}: {
  patientId?: string;
  encounterId?: string;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    CreateGenericObservationResponse,
    CreateGenericObservationParams
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.Observation.CREATE_GENERIC({
        patientId,
        encounterId,
      }),
      mutationFn: (params) => {
        return axiosInstance.post("/charting/observation-generic/", params);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Observation.DEFAULT({
            patientId,
            encounterId,
          }),
        });

        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Timeline.ENCOUNTER({
            patientId,
            motherEncounterId: encounterId,
          }),
        });
      },
    },
  });
};
